import {AttrsSchema, Component, ConfigOptions, jsx} from 'panel';
import type {VNode} from 'snabbdom';
import throttle from 'lodash/throttle';
import ResizeObserver from 'resize-observer-polyfill';
import sum from 'lodash/sum';
import isEmpty from 'lodash/isEmpty';
import debounce from 'lodash/debounce';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {
  assignColorsToSegmentArray,
  BAR_TEXT_LABEL_WIDTH,
  generateHeaders,
  getResponsiveColumnWidths,
  maximumSegmentValue,
  MIN_BAR_SEGMENT_WIDTH,
  MINIMUM_COLUMN_WIDTH,
  minimumSegmentValue,
  segmentDepth,
  segmentHasChildren,
  segmentHasNegativeValues,
  truncateSegments,
} from 'common/charts/visualization/bar-viz/util';
import {ChartTooltipAttrs, ChartTooltipEl} from 'common/components/chart-tooltip';
import {TooltipItemType} from 'common/components/chart-tooltip/types';
import {TooltipEl} from 'common/components/tooltip';
import {renderBarWithLabel} from 'common/charts/visualization/bar-viz/mixins';
import {SvgIconEl} from 'common/components/svg-icon';
import {TOOLTIP_THROTTLE_TIME} from 'common/components/chart-tooltip/util';
import {SelectEl} from 'common/components/select';
import {getTooltipFromLabel} from 'common/report/util/properties';
import {FlatSortConfig, SortBy} from 'common/report/util/sorting/types';
import {renderSegmentLabel} from 'common/templates/mixins';
import {Placement} from 'common/components/tooltip/types';

import {
  Bar,
  BarClickEvent,
  BarSegment,
  BarValueType,
  BarVizAttrs,
  BarVizJsxAttrs,
  BarVizState,
  Segment,
  SegmentWithChildren,
  SegmentWithoutChildren,
  TooltipDataCallback,
} from './types';
import css from './index.inline.styl';

const DOWNLOAD_SEGMENT_LIMIT = 30;

// tweak this to optimize if needed, more or less arbitrary for now
function getProgressivePageSize(height: number): number {
  if (height < 600) {
    return 20;
  } else {
    return 100;
  }
}

export class BarViz extends Component<BarVizState, BarVizAttrs> {
  private resizeMouseMoveHandler: (ev: MouseEvent) => void;
  private chartContentResizeObserver: ResizeObserver = null;
  private progressiveIntersectionObserver: IntersectionObserver = null;
  private hideTooltipTimeout: number;
  private chartContentEl: HTMLElement = null;

  private _chartData: Segment[];
  private _tooltipDataCallback: TooltipDataCallback;

  static get attrsSchema(): AttrsSchema<BarVizAttrs> {
    return {
      ...super.attrsSchema,
      'bar-value-type': {
        type: `string`,
        default: BarValueType.Absolute,
      },
      'show-segment-labels': {
        type: `boolean`,
        description: `whether to show labels below the bar`,
      },
      'enable-bar-click-events': `boolean`,
      'column-widths': {
        type: `json`,
        tsType: `ColumnWidths`,
        description: `Object with columnIndex (integer) keys, resizedWidth (float) values as percentages.`,
      },
      'sort-config': {type: `json`, tsType: `SortConfig`},
      'download-mode': {
        type: `boolean`,
      },
      'hide-tooltip': `boolean`,
    };
  }

  get config(): ConfigOptions<BarVizState> {
    return {
      css,
      defaultState: {
        activeSortPanelIdx: null,
        columnWidths: {},
        hoverTooltip: null,
        hasNegativeValues: false,
        chartMin: 0,
        chartMax: 0,
        resizeColumn: null,
        resizeColumnIdx: null,
        resizeColumnWidth: null,
        selfWidth: 0,
        selfHeight: 0,
        progressivePage: 0,
      },
      template: () => this.renderTemplate(),
      useShadowDom: true,
    };
  }

  renderTemplate() {
    const headerColumnsWidths = this.getColumnWidths().map((width) => `${width}px`);
    const {segments, isTruncated} = this.getChartDataToDisplay();
    const isDownloadMode = this.attr(`download-mode`);

    return (
      <div
        sel='.bar-chart'
        class={{
          'is-resizing': !!this.state.resizeColumn,
          // width may not have been calculated on first render, so we set visibility hidden
          'is-hidden': !this.state.selfWidth,
          'download-mode': isDownloadMode,
        }}
        style={{
          '--text-label-width': `${BAR_TEXT_LABEL_WIDTH}px`,
          '--min-bar-segment-width': `${MIN_BAR_SEGMENT_WIDTH}px`,
        }}
      >
        {this.renderResizeGrips()}
        <div
          sel='.chart-content'
          attrs={{elref: `chartContent`}}
          hook={{insert: (vnode) => this.onChartContentInserted(vnode.elm as HTMLElement)}}
        >
          <div
            sel='.header-bar'
            style={{
              'grid-template-columns': headerColumnsWidths.join(` `).concat(` ${this.getValueWidth()}px`),
            }}
          >
            {this.renderHeaders()}
          </div>

          <div sel='.segment-container'>{segments.map((segment) => this.getSegmentRenderer(segment, 0))}</div>

          {!!isDownloadMode && isTruncated ? (
            <div sel='.truncation-notice'>
              Results have been truncated to the first {DOWNLOAD_SEGMENT_LIMIT} segments for download.
            </div>
          ) : (
            <div
              sel='.progressive-marker'
              hook={{insert: (vnode) => this.onProgressiveMarkerInserted(vnode.elm as HTMLElement)}}
            />
          )}
        </div>

        {this.state.hoverTooltip && (
          <ChartTooltipEl attrs={this.state.hoverTooltip.attrs} props={this.state.hoverTooltip.props} />
        )}
      </div>
    );
  }

  renderHeaders() {
    const headers = generateHeaders(this.chartData[0], this.attr(`sort-config`));

    return headers.map(({label, sortOptionItems}, idx) => {
      const headerLabelEl = (
        <div
          sel='.label'
          on={{
            click: () => {
              this.update({
                activeSortPanelIdx: idx,
              });
            },
          }}
        >
          <div sel='.text'>{label}</div>
          {this.attr(`sort-config`) && (
            <div sel='.sort-icon'>
              <SvgIconEl attrs={{icon: `caret-down`}} />
            </div>
          )}
        </div>
      );

      return (
        <div sel='.header' attrs={{elref: `header`}}>
          {this.attr(`sort-config`) ? (
            <SelectEl
              attrs={{
                sections: [{items: sortOptionItems}],
                'menu-width': 144,
                elref: `headerMenu`,
              }}
              on={{
                change: (ev) => this.onSortingChange(ev, idx),
              }}
            >
              <div sel='.sort-trigger' attrs={{slot: `trigger`}}>
                {headerLabelEl}
              </div>
            </SelectEl>
          ) : (
            headerLabelEl
          )}
        </div>
      );
    });
  }

  renderResizeGrips() {
    if (!this.canResize()) {
      return null;
    }

    return this.getColumnWidths().reduce(
      (acc: {offsetWidth: number; nodes: VNode[]}, _, idx) => {
        const offsetWidth = acc.offsetWidth + this.getColumnWidth(idx);
        const left = `${offsetWidth - 12}px`;

        return {
          offsetWidth,
          nodes: acc.nodes.concat(
            <div
              sel='.header-resize-grip'
              class={{'is-resizing': this.state.resizeColumnIdx === idx}}
              style={{left}}
              on={{mousedown: (ev) => this.onResizeMouseDown(ev, idx)}}
              attrs={{elref: `resizeGrip`}}
            />,
          ),
        };
      },
      {offsetWidth: 0, nodes: []},
    ).nodes;
  }

  renderFinalSegmentBar(segment: SegmentWithoutChildren, columnIdx: number) {
    let barTemplateColumns = `100%`;
    const labelTooltip = getTooltipFromLabel(segment.label);

    if (this.state.hasNegativeValues) {
      const {chartMin, chartMax} = this.state;
      const dist = Math.abs(chartMin - chartMax);

      const percentageOfDist = (num) => (Math.abs(num) / dist) * 100;
      barTemplateColumns = `${percentageOfDist(chartMin)}% ${percentageOfDist(chartMax)}%`;
    }

    return (
      <div
        sel='.segment-wrapper.bars-wrapper'
        style={{
          'grid-template-columns': this.getNestedColumnGridTemplate(columnIdx),
        }}
        attrs={{elref: `leafSegment`}}
      >
        <div
          sel='.segment-label-block'
          attrs={{title: segment.label}}
          class={{
            'multi-bar': segment.bars.length > 1,
          }}
        >
          <div sel='.segment-label'>{renderSegmentLabel(segment.label)}</div>
          <div sel='.segment-value-label'>{segment.valueLabel}</div>
          {labelTooltip && (
            <TooltipEl
              attrs={{
                text: labelTooltip,
                placement: Placement.RightCenter,
                'delay-out': 150,
              }}
            />
          )}
        </div>
        <div
          sel='.segment-values-wrapper.bars'
          class={{'has-negative-values': this.state.hasNegativeValues}}
          style={{
            'grid-template-columns': barTemplateColumns,
          }}
        >
          {segment.bars.map((bar: Bar, idx) => {
            // if there's exactly two bars, we render the first one with reversed labels
            // to have a side-by-side visualization.
            const isFirstOfPair = idx === 0 && segment.bars.length === 2;

            // in order to ensure the subsequent bar is on the next line, add an empty div if first bar is negative.
            // this fixes a case where a negative and positive bar appear on the same line.
            const shouldAddPlaceholder = idx % 2 === 0 && bar.segments[0].value < 0;

            const barEl = this.renderBar(segment, bar, isFirstOfPair);

            if (shouldAddPlaceholder) {
              return (
                <>
                  {barEl}
                  <div />
                </>
              );
            } else {
              return barEl;
            }
          })}
        </div>
      </div>
    );
  }

  renderSegmentWithChildren(segment: SegmentWithChildren, columnIdx: number) {
    const isLeaf = !segment.children.some(segmentHasChildren);

    return (
      <div
        sel='.segment-wrapper.breakdown'
        style={{
          'grid-template-columns': this.getNestedColumnGridTemplate(columnIdx),
        }}
      >
        <div sel='.segment-label-block.breakdown' attrs={{title: segment.label}}>
          <div sel='.segment-label'>{renderSegmentLabel(segment.label)}</div>
          {segment.valueLabel && (
            <div sel='.segment-value-label'>
              <span sel='.indicator'>Overall</span>
              <span sel='.value'>{segment.valueLabel}</span>
            </div>
          )}
        </div>
        <div sel={`.segment-values-wrapper.breakdown`} class={{'leaf-segment': isLeaf}}>
          {segment.children.map((segment) => this.getSegmentRenderer(segment, columnIdx + 1))}
        </div>
      </div>
    );
  }

  getSegmentRenderer(segment: Segment, columnIdx) {
    return segmentHasChildren(segment)
      ? this.renderSegmentWithChildren(segment, columnIdx)
      : this.renderFinalSegmentBar(segment, columnIdx);
  }

  renderBar(segment: SegmentWithoutChildren, bar: Bar, isFirstOfPair: boolean) {
    const {chartMax, chartMin, hasNegativeValues} = this.state;
    const barValueType = this.attr(`bar-value-type`);
    const enableClick = this.attr(`enable-bar-click-events`);
    const showSegmentLabels = this.attr(`show-segment-labels`);

    const clickBar = (ev, bar, barSegment) => this.clickBar(segment, bar, barSegment, ev);
    const mouseMove = throttle(
      (ev, barSegment) => this.onSegmentMouseMove(ev, segment, bar, barSegment),
      TOOLTIP_THROTTLE_TIME,
      {
        leading: true,
        trailing: false,
      },
    );
    const mouseLeave = () => this.setHideTooltipTimeout();

    return renderBarWithLabel(
      bar,
      {
        hasNegativeValues,
        barValueType,
        chartMax,
        chartMin,
        showSegmentLabels,
        enableClick,
        isFirstOfPair,
      },
      {
        clickBar,
        mouseMove,
        mouseLeave,
      },
    );
  }

  onSegmentMouseMove(ev: MouseEvent, segment: SegmentWithoutChildren, bar: Bar, barSegment: BarSegment) {
    this.clearHideTooltipTimeout();

    if (this.attr(`hide-tooltip`)) {
      return;
    }

    const tooltipAttrs = this.getTooltipAttrs(segment, bar, barSegment);

    this.update({
      hoverTooltip: {
        attrs: tooltipAttrs,
        props: {positioning: {clientX: ev.clientX, clientY: ev.clientY}},
      },
    });
  }

  clickBar(segment: Segment, bar: Bar, barSegment: BarSegment, clickEvent: MouseEvent) {
    const {pageX, pageY, offsetX, offsetY} = clickEvent;

    this.dispatchEvent(
      new CustomEvent<BarClickEvent>(`barClick`, {
        detail: {
          pageX,
          pageY,
          offsetX,
          offsetY,
          segment,
          bar,
          barSegment,
        },
      }),
    );
  }

  getTooltipAttrs(segment: SegmentWithoutChildren, bar: Bar, barSegment: BarSegment): ChartTooltipAttrs {
    if (this._tooltipDataCallback) {
      return this._tooltipDataCallback(segment, bar, barSegment);
    }

    return {
      'title-color': barSegment.color,
      sections: [
        {
          rows: [
            {
              items: [{text: segment.label, type: TooltipItemType.Strong}],
            },
            {
              items: [
                {
                  text: String(barSegment.value),
                  type: TooltipItemType.Data,
                },
              ],
            },
          ],
        },
      ],
    };
  }

  connectedCallback() {
    this.onResizeMouseUp = this.onResizeMouseUp.bind(this);
    this.resetResizeState = this.resetResizeState.bind(this);

    super.connectedCallback();
  }

  onChartContentInserted(el: HTMLElement) {
    this.chartContentEl = el;
    const debouncedResizeUpdate = debounce(
      (selfWidth, selfHeight) => {
        this.update({selfWidth, selfHeight});

        // always snap scroll to the right when resizing to fit a certain number of visible segments
        if (this.chartContentEl) {
          requestAnimationFrame(() => (this.chartContentEl.scrollLeft = this.chartContentEl.scrollWidth));
        }

        this.setDefaultColumnWidths();
      },
      50,
      {leading: true, trailing: true},
    );

    this.chartContentResizeObserver = new ResizeObserver((entries) => {
      const {width, height} = entries[0].contentRect;
      debouncedResizeUpdate(width, height);
    });

    this.chartContentResizeObserver.observe(this.chartContentEl);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.chartContentResizeObserver?.disconnect();
    this.cleanResizeEventListeners();
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);

    if (attr === `column-widths`) {
      this.setDefaultColumnWidths();
    }

    if (attr === `hide-tooltip` && this.attr(`hide-tooltip`)) {
      this.update({hoverTooltip: null});
    }
  }

  onResizeMouseMove(initialX, maximumColumnWidth, ev) {
    const {columnWidths, resizeColumnIdx} = this.state;
    ev.stopPropagation();

    const delta = ev.screenX - initialX;
    let width = Math.max(columnWidths[resizeColumnIdx] + delta, MINIMUM_COLUMN_WIDTH);
    width = Math.min(width, maximumColumnWidth);

    this.update({
      resizeColumnWidth: width,
    });
  }

  onResizeMouseDown(ev, columnIdx) {
    ev.preventDefault();

    if (ev.button !== 0) {
      return;
    }

    const {columnWidths} = this.state;
    const columnElement = this.getHeaderColumn(columnIdx);
    const widthForColumn = columnElement.offsetWidth;
    columnWidths[columnIdx] = widthForColumn;

    this.update({
      resizeColumn: columnElement,
      resizeColumnIdx: columnIdx,
      resizeColumnWidth: widthForColumn,
      columnWidths,
    });

    this.resizeMouseMoveHandler = (newEvent) =>
      this.onResizeMouseMove(ev.screenX, this.calculateMaxColumnWidth(columnIdx), newEvent);
    window.addEventListener(`mousemove`, this.resizeMouseMoveHandler, true);
    window.addEventListener(`mouseup`, this.onResizeMouseUp, true);
    window.addEventListener(`blur`, this.resetResizeState, true);
  }

  onResizeMouseUp() {
    const {columnWidths, resizeColumnIdx} = this.state;

    const columnElement = this.getHeaderColumn(resizeColumnIdx);
    const updatedColumnWidths = {
      ...columnWidths,
      [resizeColumnIdx]: this.getPercentageWidth(columnElement.offsetWidth),
    };

    this.resetResizeState();
    this.dispatchEvent(
      new CustomEvent(`columnWidthChange`, {
        detail: {columnWidths: updatedColumnWidths},
      }),
    );
    this.update({columnWidths: updatedColumnWidths});
  }

  onSortingChange(ev, idx): void {
    const value: FlatSortConfig = ev.detail.item.value;
    const depth = segmentDepth(this.chartData[0]);

    let newSortConfig = this.attr(`sort-config`);

    if (idx > depth) {
      newSortConfig = {
        ...newSortConfig,
        sortBy: SortBy.Value,
        sortOrder: value.sortOrder,
      };
    } else {
      newSortConfig.colSortAttrs[idx] = value;
      newSortConfig.sortBy = SortBy.Column;
    }

    this.dispatchEvent(new CustomEvent(`sortingChange`, {detail: {sortConfig: newSortConfig}}));
  }

  onProgressiveMarkerInserted(el: HTMLElement) {
    if (this.progressiveIntersectionObserver) {
      this.progressiveIntersectionObserver.disconnect();
    }

    this.progressiveIntersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.intersectionRatio > 0) {
            this.update({progressivePage: this.state.progressivePage + 1});
          }
        }
      },
      {
        root: null,
        threshold: 0.1,
      },
    );

    this.progressiveIntersectionObserver.observe(el);
  }

  resetResizeState() {
    this.cleanResizeEventListeners();

    this.update({
      resizeColumn: null,
      resizeColumnIdx: null,
      resizeColumnWidth: 0,
    });
  }

  cleanResizeEventListeners() {
    window.removeEventListener(`mousemove`, this.resizeMouseMoveHandler, true);
    window.removeEventListener(`mouseup`, this.onResizeMouseUp, true);
    window.removeEventListener(`blur`, this.resetResizeState, true);
  }

  getColumnWidth(columnIdx: number): number {
    const columnPixelWidth = this.getPixelWidth(this.getResponsiveColumnWidths().labelWidthPercentage);

    if (!this.canResize()) {
      return columnPixelWidth;
    }

    if (this.state.resizeColumnIdx === columnIdx) {
      return this.state.resizeColumnWidth;
    }

    return this.state.columnWidths[columnIdx]
      ? this.getPixelWidth(this.state.columnWidths[columnIdx])
      : columnPixelWidth;
  }

  getColumnWidths(): number[] {
    return Array(segmentDepth(this.chartData[0]) + 1)
      .fill(null)
      .map((_, idx) => this.getColumnWidth(idx));
  }

  getValueWidth() {
    if (!this.canResize()) {
      return this.getPixelWidth(this.getResponsiveColumnWidths().valueWidthPercentage);
    }

    return this.state.selfWidth - sum(this.getColumnWidths());
  }

  /**
   * mp-bar-viz renders markup in a recursive tree structure that mirrors its chartData prop.
   * since we store column widths as percentages relative to the entire chart, we need to translate
   * those values into be relative to the widths of the remaining columns.
   *
   * TODO: consider refactoring to render in a flat table-like structure to simplify this logic
   * NOTE: attempted to render in a single grid in this PR https://github.com/mixpanel/analytics/pull/35717
   * however, certain browsers impose limits on number of grid rows you can render
   * https://bugzilla.mozilla.org/show_bug.cgi?id=1336679
   * https://bugs.chromium.org/p/chromium/issues/detail?id=688640
   */
  getNestedColumnGridTemplate(columnIdx: number): string {
    const {labelWidthPercentage, valueWidthPercentage} = this.getResponsiveColumnWidths();

    if (!this.canResize()) {
      return `${this.getPixelWidth(labelWidthPercentage)}px ${this.getPixelWidth(valueWidthPercentage)}px`;
    }

    const valueWidth = this.getValueWidth();
    const remainingColumnTotalWidth = sum([...this.getColumnWidths().slice(columnIdx + 1), valueWidth]);

    return `${this.getColumnWidth(columnIdx)}px ${remainingColumnTotalWidth}px`;
  }

  calculateMaxColumnWidth(columnIdx: number): number {
    const otherColumnWidth = this.getColumnWidths().reduce(
      (acc, curr, idx) => (idx === columnIdx ? acc : acc + curr),
      0,
    );

    // account for min width of a single bar segment, label width, and 2px buffer
    let valueBuffer = BAR_TEXT_LABEL_WIDTH + MIN_BAR_SEGMENT_WIDTH + 2;
    if (this.state.hasNegativeValues) {
      valueBuffer *= 2;
    }

    return this.state.selfWidth - otherColumnWidth - valueBuffer;
  }

  getHeaderColumn(index: number): HTMLElement {
    return this.el.querySelector(`.header:nth-child(${index + 1})`);
  }

  getPercentageWidth(widthInPx: number) {
    return (widthInPx / this.state.selfWidth) * 100;
  }

  getPixelWidth(percentage: number) {
    return (percentage / 100) * this.state.selfWidth;
  }

  getResponsiveColumnWidths() {
    const depth = segmentDepth(this.chartData[0]) + 1;
    return getResponsiveColumnWidths(this.state.selfWidth, depth);
  }

  setDefaultColumnWidths() {
    if (!this.chartData?.length || !this.state.selfWidth) {
      return;
    }

    let columnWidths;
    if (!isEmpty(this.state.columnWidths)) {
      columnWidths = this.state.columnWidths;
    } else {
      columnWidths = this.attr(`column-widths`);
    }

    if (!isEmpty(columnWidths)) {
      const minWidth = this.getPercentageWidth(MINIMUM_COLUMN_WIDTH);

      const boundColumnWidths = Object.entries<number>(columnWidths).map(([indexKey, width]) => {
        const maxWidth = this.getPercentageWidth(this.calculateMaxColumnWidth(parseInt(indexKey)));
        return [indexKey, Math.min(Math.max(width, minWidth), maxWidth)];
      });

      this.update({columnWidths: Object.fromEntries(boundColumnWidths)});
    }
  }

  // don't allow resizing if there's overflow
  canResize(): boolean {
    const {labelWidthPercentage, valueWidthPercentage} = this.getResponsiveColumnWidths();
    const depth = segmentDepth(this.chartData[0]) + 1;

    return depth * labelWidthPercentage + valueWidthPercentage <= 100;
  }

  clearHideTooltipTimeout() {
    window.clearTimeout(this.hideTooltipTimeout);
    this.hideTooltipTimeout = null;
  }

  setHideTooltipTimeout() {
    this.clearHideTooltipTimeout();
    this.hideTooltipTimeout = window.setTimeout(() => this.update({hoverTooltip: null}), 250);
  }

  getChartDataToDisplay() {
    let numToShow;
    if (this.attr(`download-mode`)) {
      numToShow = DOWNLOAD_SEGMENT_LIMIT;
    } else {
      const pageSize = getProgressivePageSize(this.state.selfHeight);
      numToShow = (this.state.progressivePage + 1) * pageSize;
    }

    return truncateSegments(this.chartData, numToShow);
  }

  get chartData(): Segment[] {
    return this._chartData;
  }

  set chartData(data) {
    const dataWithColors = assignColorsToSegmentArray(data);

    this._chartData = dataWithColors;

    const hasNegativeValues = dataWithColors.some(segmentHasNegativeValues);

    const chartMax = dataWithColors.reduce((acc, segment) => {
      const sectionMax = maximumSegmentValue(segment);
      return sectionMax > acc ? sectionMax : acc;
    }, 0);

    const chartMin = dataWithColors.reduce((acc, segment) => {
      const sectionMin = minimumSegmentValue(segment);
      return sectionMin < acc ? sectionMin : acc;
    }, Infinity);

    this.setDefaultColumnWidths();

    this.update({
      chartMax,
      chartMin,
      hasNegativeValues,
    });
  }

  get tooltipDataCallback() {
    return this._tooltipDataCallback;
  }

  set tooltipDataCallback(f: TooltipDataCallback) {
    this._tooltipDataCallback = f;
  }
}

export const BarVizEl = defineAndMakeJsxFactory<BarVizJsxAttrs>(`mp-bar-viz`, BarViz);
export const BarVizReact = panel2react<BarVizJsxAttrs>(`mp-bar-viz`, BarViz);
